import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE, WA_BOOK, scrollToId } from "../../lib/site";

const Wordmark = () => (
    <a
        href="#home"
        data-testid="header-logo-link"
        aria-label="Flammingo Luxurious Spa — home"
        onClick={(e) => {
            e.preventDefault();
            scrollToId("#home");
        }}
        className="flex items-center gap-3"
    >
        <img
            src="/logo.png"
            alt="Flammingo Luxurious Spa logo"
            className="h-11 w-11 rounded-full ring-1 ring-gold/50 object-cover"
        />
        <span className="leading-none">
            <span className="block font-serif text-lg tracking-[0.18em] text-cream">
                FLAMMINGO
            </span>
            <span className="block text-[10px] font-sans uppercase tracking-[0.42em] text-gold">
                Luxurious Spa
            </span>
        </span>
    </a>
);

const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => (e) => {
        e.preventDefault();
        setOpen(false);
        scrollToId(href);
    };

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
                    scrolled
                        ? "border-b border-gold/20 bg-emerald-950/80 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 md:px-12">
                    <Wordmark />
                    <nav
                        className="hidden items-center gap-9 lg:flex"
                        aria-label="Primary"
                    >
                        {NAV_LINKS.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={go(link.href)}
                                className="group relative text-[13px] font-sans font-light uppercase tracking-[0.22em] text-sage transition-colors duration-300 hover:text-cream"
                            >
                                {link.label}
                                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-500 group-hover:w-full" />
                            </a>
                        ))}
                    </nav>
                    <div className="flex items-center gap-4">
                        <a
                            href={WA_BOOK}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-book-button"
                            className="hidden border border-gold/60 px-6 py-2.5 text-[11px] font-sans font-medium uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-emerald-950 sm:block"
                        >
                            Book Your Experience
                        </a>
                        <button
                            type="button"
                            data-testid="mobile-menu-button"
                            aria-label={open ? "Close menu" : "Open menu"}
                            onClick={() => setOpen((v) => !v)}
                            className="text-cream lg:hidden"
                        >
                            {open ? <X size={26} /> : <Menu size={26} />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-40 flex flex-col justify-center bg-emerald-950/95 px-8 backdrop-blur-2xl lg:hidden"
                    >
                        <nav className="flex flex-col gap-2" aria-label="Mobile">
                            {NAV_LINKS.map((link, i) => (
                                <motion.a
                                    key={link.href}
                                    href={link.href}
                                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={go(link.href)}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        delay: 0.08 * i + 0.1,
                                        duration: 0.6,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    className="border-b border-gold/10 py-5 font-serif text-4xl text-cream transition-colors duration-300 hover:text-gold"
                                >
                                    {link.label}
                                </motion.a>
                            ))}
                        </nav>
                        <motion.a
                            href={WA_BOOK}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="mobile-book-button"
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.55, duration: 0.6 }}
                            className="mt-10 inline-block w-fit bg-gold px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] text-emerald-950"
                        >
                            Book Your Experience
                        </motion.a>
                        <p className="mt-12 text-xs uppercase tracking-[0.3em] text-sage/60">
                            {SITE.fullLocation}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Header;
